import { eventRuntimeApiSdk } from '@hds/client-pinia-vue-sdk';

import { autoAuctionApiSdk } from './autoAuctionClient.js';
import { deltaRealtime } from './deltaRealtime.js';
import { idOf } from './identity.js';
import { saleAssetOf } from './saleAsset.js';
import { useRuntimeEndpoints } from './backends.js';
import { useLogger } from '../adapters/ports.js';

/**
 * Bidding data source — how the Live Bidding store loads lots, receives live
 * updates, and writes bids. What is sold comes from vibe-server; how the
 * bidding on it stands comes from the REAL event runtime:
 *   - reads   → vibe-server sales and their assets (via autoAuctionApiSdk),
 *               bidding-server bidding state (via eventRuntimeApiSdk)
 *   - live    → delta-server change feed (item-bidding topic, via deltaRealtime)
 *   - writes  → bidding-server bidder actions
 *
 * Interface (kept stable so the store stays decoupled from the runtime):
 *   loadLots()                         -> { lots: [...rows], proxyBids }
 *                                         (row = mapServerLot input;
 *                                          proxyBids = { itemId: [{ badge, amount }] })
 *   subscribe({ onLotFrame })          -> teardown fn
 *   proposeBid(itemId, { amount, badge })
 *   upsertProxyBid(itemId, { eventId, amount, badge })
 *   deleteProxyBid(itemId, { eventId, badge })
 *   getProxyBids(itemId, { badges })     -> [{ badge, amount }] the runtime holds
 *                                         for that lot (prebids or live)
 *   getItemBidding(itemId)             -> the one lot's processed bidding doc
 *                                         ({ bid, badge, increment, status }),
 *                                         or null
 *
 * READS AND BIDS ONLY. There used to be a `reset()` here that wiped the whole
 * event runtime and regenerated a demo block, through the scale-load-test
 * server's apiKey-authed tools endpoints. It is gone: resetting the runtime is
 * an operator action, done from Postman or curl, and it has no business
 * shipping inside a client — least of all one shared by every surface, carrying
 * a dev API key to do it.
 */

// The endpoints this module reaches used to be `import.meta.env` reads with
// inline fallbacks, which is what tied it to a Vite app. They are read off the
// SDK's own configuration now — see backends.js for why the host keeps the
// environment and the package keeps none of it.

// The delta-server topic carrying processed per-lot bidding state.
const ITEM_BIDDING_TOPIC = 'item-bidding';
// …and the one carrying EVENT-level state. A clerk pausing a sale writes
// `status: 'PAUSED'` here and nowhere else — no item-bidding doc changes — so
// without this topic a pause is invisible to the app.
const EVENT_ACTIVITY_TOPIC = 'event-activity';

// The delta-server watches with `fullDocument: 'updateLookup'` and no projection
// of its own, so an unprojected frame carries the entire raw item-bidding doc.
// Most of that weight is `processedEntries` — an append-only engine transcript
// that grows without bound for as long as a lot stays contested, meaning the
// frames get heaviest on exactly the hot lots that produce the most of them.
// subscribe() below reads six fields; asking the server to drop the bulky ones
// keeps a busy lane's frames small instead of letting them grow all sale long.
const ITEM_BIDDING_PROJECTION = {
  type: 'exclude',
  excludeProperties: ['processedEntries', 'proxyBids', 'activeBidders']
};

// event-activity docs carry the same unbounded `processedEntries` transcript, and
// we read only id + status off a frame.
const EVENT_ACTIVITY_PROJECTION = {
  type: 'exclude',
  excludeProperties: ['processedEntries']
};


// Engine eventType -> the Live Bidding "type" facet the UI groups/filters on.
const EVENT_TYPE_LABEL = {
  automatedAuction: 'Sequential',
  simulcastAuction: 'Simulcast',
  timedAuction: 'Timed'
};

// The runtime reports announcement lights as color words; the tray/cards render
// single-letter codes (G/Y/W/…).
const LIGHT_CODE = { green: 'G', yellow: 'Y', red: 'R', white: 'W', blue: 'B' };
const mapLight = (l) => LIGHT_CODE[String(l).toLowerCase()] ?? String(l).charAt(0).toUpperCase();

const DEFAULT_FALLBACK = 'linear-gradient(135deg,#2d3340,#16161a)';

// Runtime read routes wrap their payload as { retrieved: ... }.
const envelope = (resp) => (resp && 'retrieved' in resp ? resp.retrieved : resp);

// The clerk action that produces each halt status. `reason` is a REQUIRED body
// field on both routes, so a halt raised through the clerk app always carries one.
const HALT_ENTRY_TYPE = { PAUSED: 'pauseEvent', FINISHED: 'stopEvent' };

// The clerk's stated reason for the halt an event is CURRENTLY in.
//
// The engine drops it from the event's status state — processPauseEvent takes
// the raw entry and copies only `status` off it — but the entry that carried it
// is kept verbatim on the processed-entry transcript. So the reason for this
// halt is the last transcript entry whose raw entry is the action that produced
// the current status. Matching on the action (not just "the last halt") keeps a
// pause → resume → pause cycle from reporting the older pause's reason, and
// keeps a paused sale from quoting an earlier stopEvent.
function haltReasonFor(activity) {
  const wanted = HALT_ENTRY_TYPE[activity?.status];
  if (!wanted) return null;
  const entries = activity?.processedEntries;
  if (!Array.isArray(entries)) return null;
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const rawEntry = entries[i]?.rawEntry;
    if (rawEntry?.rawEntryType === wanted) return rawEntry.reason || null;
  }
  return null;
}

// Join a sale's asset (display) and its live item-bidding (state) into the flat
// lot row the store's mapServerLot() consumes. Sale, lane, type and run number
// come from vibe-server's sale and the asset's place in it; live bid, status and
// increment from item-bidding. (event-engine-state carries per-lot bidding rules
// but is apiKey-only, so we deliberately avoid it from the client and lean on
// item-bidding instead.)
function buildRow(
  sale,
  asset,
  itemBidding,
  eventStatus,
  eventHaltReason
) {
  const saleId = idOf(sale);
  const bid = itemBidding?.bid ?? null;
  return {
    id: asset.id,
    itemId: asset.id,
    // The lot's owning event, and that event's runtime status. A sale's id IS
    // the runtime's event id, so this is what a status frame joins back to.
    eventId: saleId,
    eventStatus,
    eventHaltReason,
    sale: sale?.saleName ?? sale?.name ?? saleId,
    type: EVENT_TYPE_LABEL[sale?.eventType] ?? 'Simulcast',
    lane: sale?.laneName ?? sale?.location ?? 'Lane',
    lot: asset.runNumber,
    year: asset.year,
    make: asset.make,
    model: asset.model,
    trim: asset.trim,
    // Extra descriptive fields the Explore feed renders/searches on (the Live
    // Bidding cards ignore them).
    mileage: asset.mileage,
    vin: asset.vin,
    current: bid ?? 0,
    ask: bid ?? 0,
    // NOT `?? 100`. The engine sets an asset's increment when the asset starts,
    // so an asset that has not started carries none — and a raise built on an
    // invented $100 is one the engine refuses, because the delta has to be a
    // whole multiple of the increment it actually holds. `null` is the honest
    // answer, and every surface draws a dash for it.
    increment: itemBidding?.increment ?? null,
    mmr: asset.mmr,
    lights: asset.lights.map(mapLight),
    img: asset.imageUrls[0] ?? null,
    fallback: DEFAULT_FALLBACK,
    // A sale's run list carries catalog item ids, and the runtime keys each lot
    // by that same id, so the lot needs no translation to key the liked list or
    // the asset cache.
    assetId: asset.id,
    engineStatus: itemBidding?.status ?? 'NOT-STARTED',
    badge: itemBidding?.badge ?? null,
    bid,
    estimatedTime: itemBidding?.estimatedTime ?? null
  };
}

// `badges` scopes the proxy-bid read: the runtime lists proxies for the badges named.
async function loadLots({ badges = [] } = {}) {
  const base = eventRuntimeApiSdk.baseBiddingUri;
  // The sales (sale / lane / type / run order) must land first — the per-sale
  // asset calls key off each sale's id.
  const sales = await autoAuctionApiSdk.getSales();

  // The all-lots bidding state (bidding-server) is independent of the per-sale
  // assets (vibe-server) and hits a different origin, so fetch them in ONE
  // parallel wave. The per-sale try/catch keeps one sale that cannot answer from
  // rejecting the whole batch and emptying the board: it yields null and is
  // skipped on its own.
  const [biddings, eventActivities, myProxyBids, salesWithAssets] = await Promise.all([
    eventRuntimeApiSdk.sendRequest(`${base}/item-biddings`, 'GET').then((r) => envelope(r) || []),
    // Event-level runtime status (STARTED / PAUSED / RESUMED / FINISHED). Rides
    // the same wave — it's a small unfiltered read and the join below needs it
    // before any row is built. Soft-fails to [] on purpose: this route is newer
    // than the rest of the board, and a deployment where a bidder token can't
    // read it should lose the pause banner, not every lot.
    eventRuntimeApiSdk
      .sendRequest(`${base}/event-activitys`, 'GET')
      .then((r) => envelope(r) || [])
      .catch((e) => {
        useLogger().warn(
          { event: 'bidding.event_activity_failed', err: e },
          'event activity unavailable — the board loses its pause banner, not its assets'
        );
        return [];
      }),
    // The dealer's OWN armed proxies, for the badges the dealer holds. Neither of
    // the reads above can carry them: `GET /item-biddings` strips `proxyBids`, and the delta
    // projection below excludes them from every frame — so without this call an
    // armed max exists only for as long as the tab that set it stays open, and a
    // reload loses both the "a proxy is bidding for you" state and the ability to
    // cancel it. Soft-fails to {} for the same reason event-activitys does: a
    // deployment that can't serve it should lose the proxy state, not the board.
    (badges.length ? eventRuntimeApiSdk.getProxyBids(badges) : Promise.resolve({}))
      .catch((e) => {
        useLogger().warn(
          { event: 'bidding.proxy_bids_failed', err: e },
          'armed proxy bids unavailable — the dealer cannot see or cancel a live proxy'
        );
        return {};
      }),
    Promise.all(
      sales.map(async (sale) => {
        try {
          const assets = (await autoAuctionApiSdk.getSaleAssets(idOf(sale))).map(saleAssetOf);
          return { sale, assets };
        } catch {
          return null;
        }
      })
    ),
  ]);
  // One pass over all live per-lot state; index by lot id for the join below.
  const biddingById = new Map(biddings.map((b) => [b.id, b]));
  // An event-activity doc is keyed by the event's own id.
  const eventStatusById = new Map(
    eventActivities.map((a) => [a.id, a.status])
  );
  const eventReasonById = new Map(
    eventActivities.map((a) => [a.id, haltReasonFor(a)])
  );

  const lots = [];
  // Promise.all resolves in input order, and the server answers each sale's
  // assets in run order, so lots arrive in the lane run order the UI renders.
  for (const saleWithAssets of salesWithAssets) {
    if (!saleWithAssets) continue;
    const { sale, assets } = saleWithAssets;
    const saleId = idOf(sale);
    for (const asset of assets) {
      lots.push(
        buildRow(
          sale,
          asset,
          biddingById.get(asset.id),
          eventStatusById.get(saleId) ?? null,
          eventReasonById.get(saleId) ?? null
        )
      );
    }
  }
  // Deliberately NOT run through envelope(): unlike every other read here,
  // /proxyBids answers with the bare map itself
  // ({ itemId: [{ badge, amount }] }, filtered server-side to the badges asked
  // for) rather than a { retrieved } wrapper.
  return { lots, proxyBids: myProxyBids || {} };
}

export const biddingSource = {
  loadLots,
  // The runtime has no `lots.reset` broadcast; onReset (if passed) is unused here
  // (a fresh block is picked up on the next loadLots / reconnect instead).
  subscribe(
    { onLotFrame, onEventFrame }
  ) {
    deltaRealtime.connect(useRuntimeEndpoints().deltaWebSocketUrl);
    const off = deltaRealtime.subscribe(ITEM_BIDDING_TOPIC, (frame) => {
      const doc = frame?.changeEvent?.fullDocument;
      if (!doc?.id) return;
      onLotFrame({
        lotId: doc.id,
        bid: doc.bid,
        current: doc.bid,
        badge: doc.badge,
        increment: doc.increment,
        engineStatus: doc.status,
        estimatedTime: doc.estimatedTime
      });
    }, ITEM_BIDDING_PROJECTION);
    // Pause/resume/finish arrive on their own topic — a clerk halting the sale
    // produces no item-bidding traffic at all, so this is the only live signal.
    const offEvents = deltaRealtime.subscribe(EVENT_ACTIVITY_TOPIC, (frame) => {
      const doc = frame?.changeEvent?.fullDocument;
      if (!doc?.id) return;
      // Emit the status straight away: halting the board must not wait on a
      // round trip.
      onEventFrame?.({ eventId: doc.id, status: doc.status });
      // The clerk's reason lives on `processedEntries`, which the projection
      // above deliberately strips (it's the unbounded transcript, and carrying
      // it on every frame would undo that). Re-read the one doc to pick the
      // reason up, and only on a transition INTO a halt — rare enough that a
      // single extra GET costs nothing, unlike widening the projection.
      if (!HALT_ENTRY_TYPE[doc.status]) return;
      eventRuntimeApiSdk
        .sendRequest(`${eventRuntimeApiSdk.baseBiddingUri}/event-activity/${encodeURIComponent(doc.id)}`, 'GET')
        .then((r) => {
          const activity = envelope(r);
          const reason = haltReasonFor(activity);
          // Re-emit with the reason; a status that changed underneath us since
          // the fetch is ignored, so a quick pause→resume can't strand a banner.
          if (reason && activity?.status === doc.status) {
            onEventFrame?.({ eventId: doc.id, status: doc.status, reason });
          }
        })
        .catch((e) =>
          useLogger().warn(
            { event: 'bidding.pause_reason_failed', saleId: doc.id, err: e },
            'pause reason could not be read'
          )
        );
    }, EVENT_ACTIVITY_PROJECTION);
    return () => {
      if (typeof off === 'function') off();
      if (typeof offEvents === 'function') offEvents();
    };
  },
  proposeBid: (itemId, { amount, badge }) => eventRuntimeApiSdk.proposeBid(itemId, badge, amount),
  upsertProxyBid: (itemId, { eventId, amount, badge }) => eventRuntimeApiSdk.upsertProxyBid(eventId, itemId, badge, amount),
  deleteProxyBid: (itemId, { eventId, badge }) => eventRuntimeApiSdk.deleteProxyBid(eventId, itemId, badge),
  // Not caught: a failed read means "unknown", and only the caller can say what that costs.
  getProxyBids: async (itemId, { badges }) => {
    const response = await eventRuntimeApiSdk.getProxyBidsByItemId(itemId, badges);
    return response?.[itemId] ?? [];
  },
  // ONE lot's processed bidding state, read straight from bidding-server.
  //
  // The delta feed is how live state normally arrives, and a rejected bid is
  // the case it cannot cover: the engine refuses the proposal, writes nothing
  // to the document but its own transcript, and no frame is produced. So the
  // store's bid confirmation ends on this read when no frame moved — the same
  // shape the pause reason already uses (the event-activity read in
  // subscribe() above), and for the same reason.
  //
  // Uncaught, like getProxyBids: a failed read means "unknown", and only the
  // caller can say what that costs.
  getItemBidding: async (itemId) => {
    const response = await eventRuntimeApiSdk.sendRequest(
      `${eventRuntimeApiSdk.baseBiddingUri}/item-bidding/${encodeURIComponent(itemId)}`,
      'GET'
    );
    return envelope(response) ?? null;
  }
};
